import React, { useState } from 'react';
import { styled } from '@stitches/react';
import { useTranslation } from 'react-i18next';

// 页面容器
const PageContainer = styled('div', {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '3rem'
});

// 页面标题区块
const TitleSection = styled('section', {
  textAlign: 'center',
  marginBottom: '2rem'
});

// 标题
const Title = styled('h1', {
  fontSize: '2.5rem',
  fontWeight: 'bold',
  marginBottom: '1rem',
  '@media (max-width: 768px)': {
    fontSize: '2rem'
  }
});

// 副标题
const Subtitle = styled('p', {
  fontSize: '1.2rem',
  maxWidth: '800px',
  margin: '0 auto',
  color: '$textSecondary',
  lineHeight: 1.6
});

// 选项卡容器
const TabContainer = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  gap: '1rem',
  marginBottom: '2rem',
  flexWrap: 'wrap'
});

// 选项卡按钮
const TabButton = styled('button', {
  padding: '0.75rem 1.5rem',
  border: 'none',
  borderRadius: '8px',
  fontWeight: 'medium',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  backgroundColor: '$backgroundSecondary',
  color: '$textPrimary',
  '&:hover': {
    backgroundColor: '$backgroundHover'
  },
  variants: {
    active: {
      true: {
        backgroundColor: '$primary',
        color: 'white'
      }
    }
  }
});

// 设施内容容器
const FacilityContent = styled('div', {
  marginTop: '2rem'
});

// 图片滑块容器
const GalleryContainer = styled('div', {
  position: 'relative',
  marginBottom: '3rem',
  borderRadius: '12px',
  overflow: 'hidden',
  height: '400px',
  '@media (max-width: 768px)': {
    height: '250px'
  }
});

// 大图片
const MainImage = styled('img', {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  borderRadius: '12px'
});

// 图片导航箭头基础样式
const ArrowBase = styled('button', {
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  width: '50px',
  height: '50px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(0, 0, 0, 0.4)',
  color: 'white',
  border: 'none',
  borderRadius: '50%',
  cursor: 'pointer',
  transition: 'background-color 0.2s ease',
  zIndex: 2,
  fontSize: '1.5rem',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.6)'
  },
  '@media (max-width: 768px)': {
    width: '40px',
    height: '40px',
    fontSize: '1.25rem'
  }
});

// 左箭头
const LeftArrow = styled(ArrowBase, {
  left: '20px'
});

// 右箭头
const RightArrow = styled(ArrowBase, {
  right: '20px'
});

// 缩略图容器
const ThumbnailsContainer = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  gap: '1rem',
  marginTop: '1rem'
});

// 缩略图
const Thumbnail = styled('img', {
  width: '80px',
  height: '60px',
  objectFit: 'cover',
  borderRadius: '4px',
  cursor: 'pointer',
  opacity: 0.6,
  transition: 'opacity 0.2s ease, transform 0.2s ease',
  '&:hover': {
    opacity: 0.9,
    transform: 'scale(1.05)'
  },
  variants: {
    active: {
      true: {
        opacity: 1,
        border: '2px solid $primary'
      }
    }
  },
  '@media (max-width: 768px)': {
    width: '60px',
    height: '45px'
  }
});

// 设施描述容器
const FacilityDescriptionContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '2rem'
});

// 设施标题
const FacilityTitle = styled('h2', {
  fontSize: '2rem',
  fontWeight: 'bold',
  marginBottom: '1rem',
  '@media (max-width: 768px)': {
    fontSize: '1.5rem'
  }
});

// 设施信息区域
const FacilityInfoSection = styled('div', {
  display: 'flex',
  gap: '2rem',
  '@media (max-width: 768px)': {
    flexDirection: 'column'
  }
});

// 设施信息列
const FacilityInfoColumn = styled('div', {
  flex: 1
});

// 设施详情标题
const FacilityInfoTitle = styled('h3', {
  fontSize: '1.25rem',
  fontWeight: 'bold',
  marginBottom: '1rem'
});

// 设施详情文本
const FacilityDescription = styled('p', {
  fontSize: '1rem',
  lineHeight: 1.6,
  color: '$textSecondary',
  marginBottom: '1.5rem'
});

// 设施特性列表
const FacilityFeatureList = styled('ul', {
  listStyle: 'none',
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem'
});

// 设施特性项
const FacilityFeatureItem = styled('li', {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '0.75rem',
  fontSize: '1rem',
  lineHeight: 1.5
});

// 设施特性图标
const FeatureIcon = styled('span', {
  color: '$primary',
  fontWeight: 'bold',
  fontSize: '1.25rem'
});

// 设施规格表
const SpecsTable = styled('table', {
  width: '100%',
  borderCollapse: 'collapse',
  marginTop: '1rem'
});

// 表行
const SpecsRow = styled('tr', {
  '&:not(:last-child)': {
    borderBottom: '1px solid $border'
  }
});

// 表格左侧单元格
const SpecsLabel = styled('td', {
  padding: '0.75rem 1rem 0.75rem 0',
  fontWeight: 'bold',
  width: '40%'
});

// 表格右侧单元格
const SpecsValue = styled('td', {
  padding: '0.75rem 0'
});

// 设施地图容器
const MapContainer = styled('div', {
  marginTop: '2rem',
  width: '100%',
  height: '300px',
  borderRadius: '8px',
  overflow: 'hidden'
});

// 地图图片
const MapImage = styled('img', {
  width: '100%',
  height: '100%',
  objectFit: 'cover'
});

/**
 * 矿场设施页面组件
 * 展示各个矿场设施的详细信息和环境
 */
const MiningHotelFacilities: React.FC = () => {
  const { t } = useTranslation();
  // 选择当前矿场
  const [currentFacility, setCurrentFacility] = useState<string>('russia');
  // 当前图片索引
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);
  
  // 处理选项卡点击
  const handleTabClick = (facility: string) => {
    setCurrentFacility(facility);
    setCurrentImageIndex(0);
  };
  
  // 矿场数据
  const facilities = {
    russia: {
      name: t('products.miningHotel.facilities.russia.name'),
      location: t('products.miningHotel.facilities.russia.location'),
      description: t('products.miningHotel.facilities.russia.description'),
      features: [
        t('products.miningHotel.facilities.russia.feature1'),
        t('products.miningHotel.facilities.russia.feature2'),
        t('products.miningHotel.facilities.russia.feature3'),
        t('products.miningHotel.facilities.russia.feature4')
      ],
      specs: {
        capacity: '100+ MW',
        cooling: t('products.miningHotel.facilities.russia.cooling'),
        security: t('products.miningHotel.facilities.russia.security'),
        uptime: '99.98%',
        network: '10 Gbps',
        power: t('products.miningHotel.facilities.russia.power')
      },
      images: [
        '/images/products/russia-facility-1.jpg',
        '/images/products/russia-facility-2.jpg',
        '/images/products/russia-facility-3.jpg',
        '/images/products/russia-facility-4.jpg'
      ],
      mapImage: '/images/products/russia-map.jpg'
    },
    texas: {
      name: t('products.miningHotel.facilities.texas.name'),
      location: t('products.miningHotel.facilities.texas.location'),
      description: t('products.miningHotel.facilities.texas.description'),
      features: [
        t('products.miningHotel.facilities.texas.feature1'),
        t('products.miningHotel.facilities.texas.feature2'),
        t('products.miningHotel.facilities.texas.feature3'),
        t('products.miningHotel.facilities.texas.feature4')
      ],
      specs: {
        capacity: '50 MW',
        cooling: t('products.miningHotel.facilities.texas.cooling'),
        security: t('products.miningHotel.facilities.texas.security'),
        uptime: '99.95%',
        network: '5 Gbps',
        power: t('products.miningHotel.facilities.texas.power')
      },
      images: [
        '/images/products/texas-facility-1.jpg',
        '/images/products/texas-facility-2.jpg',
        '/images/products/texas-facility-3.jpg',
        '/images/products/texas-facility-4.jpg'
      ],
      mapImage: '/images/products/texas-map.jpg'
    },
    iceland: {
      name: t('products.miningHotel.facilities.iceland.name'),
      location: t('products.miningHotel.facilities.iceland.location'),
      description: t('products.miningHotel.facilities.iceland.description'),
      features: [
        t('products.miningHotel.facilities.iceland.feature1'),
        t('products.miningHotel.facilities.iceland.feature2'),
        t('products.miningHotel.facilities.iceland.feature3'),
        t('products.miningHotel.facilities.iceland.feature4')
      ],
      specs: {
        capacity: '30 MW',
        cooling: t('products.miningHotel.facilities.iceland.cooling'),
        security: t('products.miningHotel.facilities.iceland.security'),
        uptime: '99.99%',
        network: '10 Gbps',
        power: t('products.miningHotel.facilities.iceland.power')
      },
      images: [
        '/images/products/iceland-facility-1.jpg',
        '/images/products/iceland-facility-2.jpg',
        '/images/products/iceland-facility-3.jpg',
        '/images/products/iceland-facility-4.jpg'
      ],
      mapImage: '/images/products/iceland-map.jpg'
    },
    kazakhstan: {
      name: t('products.miningHotel.facilities.kazakhstan.name'),
      location: t('products.miningHotel.facilities.kazakhstan.location'),
      description: t('products.miningHotel.facilities.kazakhstan.description'),
      features: [
        t('products.miningHotel.facilities.kazakhstan.feature1'),
        t('products.miningHotel.facilities.kazakhstan.feature2'),
        t('products.miningHotel.facilities.kazakhstan.feature3'),
        t('products.miningHotel.facilities.kazakhstan.feature4')
      ],
      specs: {
        capacity: '60 MW',
        cooling: t('products.miningHotel.facilities.kazakhstan.cooling'),
        security: t('products.miningHotel.facilities.kazakhstan.security'),
        uptime: '99.9%',
        network: '5 Gbps',
        power: t('products.miningHotel.facilities.kazakhstan.power')
      },
      images: [
        '/images/products/kazakhstan-facility-1.jpg',
        '/images/products/kazakhstan-facility-2.jpg',
        '/images/products/kazakhstan-facility-3.jpg',
        '/images/products/kazakhstan-facility-4.jpg'
      ],
      mapImage: '/images/products/kazakhstan-map.jpg'
    }
  };
  
  // 当前显示的矿场
  const currentFacilityData = facilities[currentFacility as keyof typeof facilities];
  
  // 切换到下一张图片
  const nextImage = () => {
    setCurrentImageIndex((prevIndex) => (prevIndex + 1) % currentFacilityData.images.length);
  };
  
  // 切换到上一张图片
  const prevImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === 0 ? currentFacilityData.images.length - 1 : prevIndex - 1
    );
  };
  
  return (
    <PageContainer>
      <TitleSection>
        <Title>{t('products.miningHotel.facilities.title')}</Title>
        <Subtitle>{t('products.miningHotel.facilities.subtitle')}</Subtitle>
      </TitleSection>
      
      <TabContainer>
        <TabButton
          active={currentFacility === 'russia'}
          onClick={() => handleTabClick('russia')}
        >
          {t('products.miningHotel.facilities.russia.tabName')}
        </TabButton>
        <TabButton
          active={currentFacility === 'texas'}
          onClick={() => handleTabClick('texas')}
        >
          {t('products.miningHotel.facilities.texas.tabName')}
        </TabButton>
        <TabButton
          active={currentFacility === 'iceland'}
          onClick={() => handleTabClick('iceland')}
        >
          {t('products.miningHotel.facilities.iceland.tabName')}
        </TabButton>
        <TabButton
          active={currentFacility === 'kazakhstan'}
          onClick={() => handleTabClick('kazakhstan')}
        >
          {t('products.miningHotel.facilities.kazakhstan.tabName')}
        </TabButton>
      </TabContainer>
      
      <FacilityContent>
        {/* 图片滑块 */}
        <GalleryContainer>
          <MainImage 
            src={currentFacilityData.images[currentImageIndex]} 
            alt={`${currentFacilityData.name} ${currentImageIndex + 1}`} 
          />
          <LeftArrow onClick={prevImage}>&#10094;</LeftArrow>
          <RightArrow onClick={nextImage}>&#10095;</RightArrow>
        </GalleryContainer>
        
        <ThumbnailsContainer>
          {currentFacilityData.images.map((image, index) => (
            <Thumbnail 
              key={index} 
              src={image} 
              alt={`Thumbnail ${index + 1}`}
              active={index === currentImageIndex}
              onClick={() => setCurrentImageIndex(index)}
            />
          ))}
        </ThumbnailsContainer>
        
        {/* 设施详情 */}
        <FacilityDescriptionContainer>
          <FacilityTitle>{currentFacilityData.name}</FacilityTitle>
          
          <FacilityInfoSection>
            <FacilityInfoColumn>
              <FacilityInfoTitle>{t('products.miningHotel.facilities.about')}</FacilityInfoTitle>
              <FacilityDescription>{currentFacilityData.description}</FacilityDescription>
              
              <FacilityInfoTitle>{t('products.miningHotel.facilities.features')}</FacilityInfoTitle>
              <FacilityFeatureList>
                {currentFacilityData.features.map((feature, index) => (
                  <FacilityFeatureItem key={index}>
                    <FeatureIcon>✓</FeatureIcon>
                    {feature}
                  </FacilityFeatureItem>
                ))}
              </FacilityFeatureList>
            </FacilityInfoColumn>
            
            <FacilityInfoColumn>
              <FacilityInfoTitle>{t('products.miningHotel.facilities.specifications')}</FacilityInfoTitle>
              <SpecsTable>
                <tbody>
                  <SpecsRow>
                    <SpecsLabel>{t('products.miningHotel.facilities.capacity')}</SpecsLabel>
                    <SpecsValue>{currentFacilityData.specs.capacity}</SpecsValue>
                  </SpecsRow>
                  <SpecsRow>
                    <SpecsLabel>{t('products.miningHotel.facilities.cooling')}</SpecsLabel>
                    <SpecsValue>{currentFacilityData.specs.cooling}</SpecsValue>
                  </SpecsRow>
                  <SpecsRow>
                    <SpecsLabel>{t('products.miningHotel.facilities.security')}</SpecsLabel>
                    <SpecsValue>{currentFacilityData.specs.security}</SpecsValue>
                  </SpecsRow>
                  <SpecsRow>
                    <SpecsLabel>{t('products.miningHotel.facilities.uptime')}</SpecsLabel>
                    <SpecsValue>{currentFacilityData.specs.uptime}</SpecsValue>
                  </SpecsRow>
                  <SpecsRow>
                    <SpecsLabel>{t('products.miningHotel.facilities.network')}</SpecsLabel>
                    <SpecsValue>{currentFacilityData.specs.network}</SpecsValue>
                  </SpecsRow>
                  <SpecsRow>
                    <SpecsLabel>{t('products.miningHotel.facilities.power')}</SpecsLabel>
                    <SpecsValue>{currentFacilityData.specs.power}</SpecsValue>
                  </SpecsRow>
                </tbody>
              </SpecsTable>
              
              <FacilityInfoTitle>{t('products.miningHotel.facilities.location')}</FacilityInfoTitle>
              <FacilityDescription>{currentFacilityData.location}</FacilityDescription>
              
              <MapContainer>
                <MapImage src={currentFacilityData.mapImage} alt={`${currentFacilityData.name} location map`} />
              </MapContainer>
            </FacilityInfoColumn>
          </FacilityInfoSection>
        </FacilityDescriptionContainer>
      </FacilityContent>
    </PageContainer>
  );
};

export default MiningHotelFacilities; 